"use client";

import { useLabel, useValidationClasses } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import classNames from "classnames/dedupe";
import { useId, useEffect, useMemo } from "react";
import { useAnswer, useVisibility } from "../../../../features/questionnaire";
import { useQuestionRefs } from "../../../../hooks/useQuestionRefs";
import { SecurityLocks } from "../../SecurityLocks";
import { HelpLink } from "./HelpLink";
import type {
  QuestionFactoryProps,
  QuestionLabelProps,
} from "./QuestionFactory";
import { QuestionFactory } from "./QuestionFactory";
import { getQuestionMap } from "./QuestionFactory/questions";
import type { QuestionProps } from "./types";
import { QuestionChatBox, QuestionLabel } from ".";
import "./Question.css";

// Display types that only require the simple wrapped rendering (no label/help extras)
const SIMPLE_WRAPPER_TYPES = new Set([
  "info",
  "trade_search",
  "trade_search_ai",
  "personalised_cover",
  "agreement",
]);

export const Question = (props: QuestionProps) => {
  const {
    id,
    name,
    sectionId,
    primaryText,
    secondaryText,
    tertiaryText,
    errorMessage,
    helpInfo,
    placeholder,
    shouldUpdate,
    readOnly,
    required,
    display,
    options,
    isInvalid,
    className,
    validation,
    trackAs,
    associatedQuestionName,
    chatConfig,
    suggested,
    shouldAnimate = false,
    chatbotPrompts,
  } = props;

  const answerValue = useAnswer(name);
  // Atom visibility → props.visible (stamped by processQuestionnaire) → false
  const visible = useVisibility(name) ?? props.visible ?? false;

  const isTradeDefinitionQuestion =
    display.type === "info" && name.endsWith(" definition");

  const secondaryTextId = useId();
  const tertiaryTextId = useId();
  const classes = classNames(
    "mobius",
    "mobius-question",
    {
      "mobius-question--animate": shouldAnimate && !isTradeDefinitionQuestion,
      "mobius-question--trade-definition": isTradeDefinitionQuestion,
    },
    className,
  );
  const validationClasses = useValidationClasses({ isInvalid });
  const labelClasses = classNames("mobius-question__label", validationClasses);

  // Create and register a ref for this question (memoized to prevent recreation)
  const { registerQuestionRef, unregisterQuestionRef } = useQuestionRefs();
  const questionRef = useMemo(
    () => registerQuestionRef(name),
    [name, registerQuestionRef],
  );

  // Cleanup ref when component unmounts
  useEffect(() => {
    return () => {
      unregisterQuestionRef(name);
    };
  }, [name, unregisterQuestionRef]);

  const { labelElementType } = getQuestionMap()[display.type] ?? {};
  const { labelProps, fieldProps } = useLabel({
    label: primaryText,
    id,
    labelElementType,
  });
  const { snowplow } = useSnowplowContext();

  if (!visible && !suggested) return null;

  const isSecurityLockQuestion = id === "sof_door_locks";
  const showSecondaryText = secondaryText && !isSecurityLockQuestion;

  const questionLabelProps: QuestionLabelProps = {
    primaryText,
    secondaryText: showSecondaryText ? secondaryText : undefined,
    tertiaryText,
    labelClasses,
    labelElementType,
    labelProps,
    secondaryTextId,
    tertiaryTextId: tertiaryText ? tertiaryTextId : undefined,
  };

  const onOpenDrawer = () => {
    snowplow?.trigger("helpTextOpened", {
      label: helpInfo?.label,
      helpText: helpInfo?.headline, //schema getting broken here
      primaryText,
    });
  };

  const questionFactoryProps: QuestionFactoryProps = {
    display,
    options,
    shouldUpdate,
    trackAs,
    associatedQuestionName,
    suggested,
    sectionId,
    chatbotPrompts,
    questionFieldProps: {
      id,
      name,
      placeholder,
      readOnly,
      isRequired: required,
      "aria-describedby":
        showSecondaryText || tertiaryText
          ? [
              showSecondaryText && secondaryTextId,
              tertiaryText && tertiaryTextId,
            ]
              .filter(Boolean)
              .join(" ")
          : undefined,
      value: answerValue,
      validation,
      errorMessage,
      questionLabelProps: questionLabelProps,
      helpInfo: helpInfo ? { ...helpInfo, onOpenDrawer } : undefined,
      ...fieldProps,
    },
  };

  if (SIMPLE_WRAPPER_TYPES.has(display.type)) {
    return (
      <div className={classes} ref={questionRef}>
        <QuestionFactory {...questionFactoryProps} />
      </div>
    );
  }

  return (
    <div className={classes} ref={questionRef}>
      <QuestionLabel {...questionLabelProps} />
      {helpInfo && <HelpLink {...helpInfo} onOpenDrawer={onOpenDrawer} />}
      {isSecurityLockQuestion && <SecurityLocks />}
      <QuestionFactory {...questionFactoryProps} />
      {chatConfig && (
        <QuestionChatBox chatConfig={chatConfig} questionName={name} />
      )}
    </div>
  );
};
